import { isActivePreApprovalSubmission } from '@servicetitan/financing-utils-shared';
import { useSecondLookIntegration } from '../../queries';
import { SetupDataStore } from '../../stores/setupDataStore';
import * as providerConfigs from './configs';

export const useProviderConfig = (overrideProvider?: string) => {
    const { setupData } = SetupDataStore.useStore();
    const provider = overrideProvider ?? setupData?.provider;
    const secondLookIntegration = useSecondLookIntegration();

    let providerKey;

    switch (provider?.toLowerCase()) {
        case 'servicefinance':
            // ServiceFinance always uses the embedded flow.
            // Second-look features (income type, disclosure) are controlled dynamically via secondLookIntegration.
            providerKey = 'serviceFinance';
            break;
        case 'goodleap':
            // GoodLeap always uses the embedded flow. Turns features (industry field, disclosure)
            // are controlled dynamically via secondLookIntegration.
            providerKey = 'goodLeap';
            break;
        case 'greensky':
            // GreenSky config selection logic:
            // 1. greenskyPrequalification: Prequal flow — used when actively applying for prequalification
            //    OR when continuing a prequalification from the banner (prequalificationId present but
            //    NOT transitioned to hard app). This ensures hasMailingAddressStep=true aligns with
            //    PrequalificationWorkflow which always renders a mailing address step; without this,
            //    address resolution reads from jobDetails (stale) instead of mailingAddress (user-updated),
            //    causing wrong address sent to backend and missing co-applicant marriage options.
            //    IMPORTANT: When hasTransitionedToHardApp is true, the user entered their address via
            //    Job Details (not mailing address), so we must NOT use greenskyPrequalification — that
            //    would read from the mailingAddress path which was never updated.
            // 2. greenSkyEmbedded/greenSkyEmbeddedTechnician: Embedded flow with PIN challenge
            //    - GreenSky always uses the embedded flow (regardless of BU/tenant settings)
            //    - Turns waterfall (on decline) only happens when fully enabled (requires BU + tenant settings)
            //    - An active pre-approval credit application is a hard app, so it also resolves here
            //      even when isApplyingForPrequalification is true: preApprovalId is the single source
            //      of truth for the hard/soft decision (matching the submission/refresh hooks), so the
            //      soft flag is never mutated and a later regular plan returns to greenskyPrequalification.
            // 3. greenSky: Legacy external application flow
            providerKey =
                !isActivePreApprovalSubmission(setupData) &&
                (setupData?.isApplyingForPrequalification ||
                    (setupData?.prequalificationId && !setupData?.hasTransitionedToHardApp))
                    ? 'greenskyPrequalification'
                    : setupData?.role === 'technician'
                      ? 'greenSkyEmbeddedTechnician'
                      : 'greenSkyEmbedded';
            break;
        case 'synchrony':
            providerKey =
                setupData?.role === 'technician'
                    ? 'synchronyTechnician'
                    : setupData?.prequalificationId
                      ? 'synchronyPrequalification'
                      : 'synchrony';
            break;
        case 'wellsfargo':
            // Uses isEnabled so the embedded prequal form appears only when the second-look provider
            // is fully enabled (BU + tenant + not degraded, and for Finturf the
            // wellsFargoFinturfSecondLook flag). Otherwise fall back to the plain WF config.
            providerKey = secondLookIntegration.isEnabled
                ? 'wellsFargoPrequalWithTurns'
                : 'wellsFargo';
            break;
        case 'turns':
            providerKey = 'turns' as const;
            break;
        case 'finturf':
            providerKey =
                setupData?.role === 'technician' ? 'finturfTechnician' : ('finturf' as const);
            break;
        case 'financeit':
            providerKey = 'financeit' as const;
            break;
        case 'buyfin':
            providerKey =
                setupData?.role === 'technician' ? 'buyFinTechnician' : ('buyFin' as const);
            break;
        case 'affirm':
            providerKey = 'affirm' as const;
            break;
    }

    if (!providerKey) {
        throw new Error(`Provider not found: ${provider ?? 'undefined or missing'}`);
    }

    return providerConfigs[providerKey as keyof typeof providerConfigs];
};
